import { BELL, CHAT, ENVELOPE } from '../components/icons';

/**
 * The delivery channels a notification can leave the app through, and the
 * ONE place their identity is written down: a cell in every ChannelBank, a
 * column caption over every list, a master switch on the account page.
 *
 * In-app is deliberately NOT here. Every notification writes an inbox row
 * unconditionally — that row is the ledger the escalation and the responses
 * hang off (docs/plans/notifications.md, Decision 11) — so it is not one of
 * the toggles, and a row with all three off honestly reads "Inbox only"
 * rather than pretending someone muted the thing.
 *
 * **Availability is the API's word, never ours.** The catalog sends
 * `channels[]` with `available` and a `reason` per channel; the day the
 * sms module flips to `status: 'ga'` on the server, every cell in every
 * list lights up and nothing in the web client changes. What lives here is
 * the part the API does not send: the glyph, and a label to fall back on.
 */
export const NOTIFICATION_CHANNELS = [
	{ key: 'email', label: 'Email', glyph: ENVELOPE, order: 20 },
	{ key: 'sms', label: 'Text', glyph: CHAT, order: 30 },
	{ key: 'push', label: 'Push', glyph: BELL, order: 40 },
];

export const CHANNEL_KEYS = NOTIFICATION_CHANNELS.map(channel => channel.key);

const BY_KEY = new Map(NOTIFICATION_CHANNELS.map(channel => [channel.key, channel]));

/**
 * What a channel that cannot deliver says. Keyed by the API's `reason` code
 * so a reason we have never seen still renders a sentence rather than a
 * code — and so the one paragraph above the list (the thing every disabled
 * cell points its `aria-describedby` at) is written once.
 */
export const CHANNEL_UNAVAILABLE_REASONS = {
	channel_not_built: "Text messages and push aren't delivering yet. Email carries these in the meantime.",
	no_address: 'Add and verify an address for this channel to turn it on.',
};

export const DEFAULT_UNAVAILABLE_REASON = "This channel isn't delivering yet. Email carries these in the meantime.";

export const describeChannelReason = reason => CHANNEL_UNAVAILABLE_REASONS[reason] || DEFAULT_UNAVAILABLE_REASON;

// The availability the client assumes when the catalog says nothing at all
// (an old API, a test double). Today's truth, and the real switch is the
// server's channel registry.
//
// ⚠ IT IS NOT THE ONLY COPY, whatever an earlier reading of this line said.
// Two surfaces hold today's truth in their own markup because they have no
// catalog to ask: `pages/AccountNotifications` draws the Text and Push masters
// as off and disabled (there is no read for a non-email master's stored value,
// so a live switch there would show ON for a channel somebody had turned off),
// and `NotificationEditorPage` greys the bank cells of whatever this says is
// dead. On the day sms ships, all three move together.
const ASSUMED_AVAILABILITY = { email: true, sms: false, push: false };

/**
 * The catalog's channel list merged over the registry: the API decides which
 * channels exist, what they are called and whether they deliver; the registry
 * supplies the glyph and the ordering. A channel the API adds tomorrow renders
 * with the bell rather than nothing.
 */
export function resolveChannels(catalogChannels) {
	const fromApi = Array.isArray(catalogChannels) ? catalogChannels.filter(Boolean) : [];
	const source = fromApi.length ? fromApi : NOTIFICATION_CHANNELS.map(channel => ({
		key: channel.key,
		label: channel.label,
		available: ASSUMED_AVAILABILITY[channel.key] !== false,
		reason: ASSUMED_AVAILABILITY[channel.key] === false ? 'channel_not_built' : undefined,
		order: channel.order,
	}));

	return source
		.map(channel => {
			const known = BY_KEY.get(channel.key);
			return {
				key: channel.key,
				label: channel.label || known?.label || channel.key,
				glyph: known?.glyph || BELL,
				order: Number.isFinite(channel.order) ? channel.order : (known?.order ?? 99),
				available: channel.available !== false,
				reason: channel.available === false ? (channel.reason || 'channel_not_built') : null,
			};
		})
		.sort((a, b) => a.order - b.order);
}
